import { CommonModule, isPlatformBrowser } from '@angular/common';
import {
  Component,
  OnDestroy,
  OnInit,
  Inject,
  PLATFORM_ID,
  ChangeDetectorRef,
  ChangeDetectionStrategy,
} from '@angular/core';
import { Router, RouterModule } from '@angular/router';
import { Subject, Subscription } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { AuthService } from '../../services/auth.service';
import { BehaviourSubjectService } from '../../services/behaviour-subject.service';
import {
  ReactiveFormsModule,
  FormBuilder,
  FormGroup,
  Validators,
} from '@angular/forms';
import { ApiService } from '../../services/api.service';
import { LoaderService } from '../../services/loader.service';
import { NotificationService } from '../../services/notification.service';

@Component({
  selector: 'app-footer',
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule, RouterModule],
  templateUrl: './footer.component.html',
  styleUrl: './footer.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class FooterComponent implements OnInit, OnDestroy {
  private pendingNavigation:
    | { route: string; queryParams?: Record<string, string> }
    | null = null;
  private readonly subscriptions = new Subscription();
  private destroy$ = new Subject<void>();

  // Popup form properties
  isFormOpen = false;
  partForm!: FormGroup;
  brands: any[] = [];
  modelList: any[] = [];
  yearList: string[] = [];
  categoryList: any[] = [];
  fileName: string = '';
  fileValidationErrors: string[] = [];
  isSubmitting: boolean = false;

  constructor(
    private router: Router,
    private authService: AuthService,
    private behaviourSubject: BehaviourSubjectService,
    private fb: FormBuilder,
    private apiService: ApiService,
    private loaderService: LoaderService,
    @Inject(PLATFORM_ID) private platformId: Object,
    private notificationService: NotificationService,
    private cdr: ChangeDetectorRef,
  ) {
    this.initializeForm();
  }

  ngOnInit(): void {
    this.authService.isLoggedIn$
      .pipe(takeUntil(this.destroy$))
      .subscribe((isLoggedIn) => {
        if (isLoggedIn && this.pendingNavigation) {
          const target = this.pendingNavigation;
          this.pendingNavigation = null;
          this.notificationService.success('Login successful! Redirecting...');
          this.navigateToProtectedRoute(target.route, target.queryParams);
        }
        this.cdr.markForCheck();
      });
  }

  initializeForm(): void {
    this.partForm = this.fb.group({
      partName: [
        '',
        [Validators.required, Validators.pattern(/^[a-zA-Z0-9 ]+$/)],
      ],
      brand: ['', Validators.required],
      vehicleModel: ['', Validators.required],
      year: ['', Validators.required],
      category: ['', Validators.required],
      attachment: [''],
      remarks: [''],
    });
  }

  ngOnDestroy(): void {
    // Clean up all subscriptions
    this.destroy$.next();
    this.destroy$.complete();
    this.subscriptions.unsubscribe();
  }

  navigateToRefundPolicy() {
    this.router.navigate(['/terms-condition'], {
      queryParams: { tab: 'returns' },
    });
  }

  gotoproductList(type: string): void {
    localStorage.removeItem('category');
    localStorage.removeItem('filterState');
    localStorage.removeItem('guideSearchData');
    localStorage.removeItem('seemore');
    localStorage.removeItem('SpareCategory');
    localStorage.removeItem('mostSearchedModel');
    localStorage.removeItem('categoryType');
    localStorage.removeItem('MyGarageData');
    localStorage.setItem('categoryType', JSON.stringify(type));

    // this.notificationService.info(`Browsing ${type} products`);

    if (this.router.url.startsWith('/product-list')) {
      this.router.navigateByUrl('/', { skipLocationChange: true }).then(() => {
        this.router.navigate(['/product-list']);
      });
    } else {
      this.router.navigate(['/product-list']);
    }
  }

  handleProtectedNavigation(
    event: Event,
    route: string,
    queryParams?: Record<string, string>,
  ): void {
    event.preventDefault();

    if (this.authService.isLoggedIn) {
      this.navigateToProtectedRoute(route, queryParams);
      return;
    }

    this.pendingNavigation = { route, queryParams };
    this.behaviourSubject.openLoginModal('other');
  }

  private navigateToProtectedRoute(
    route: string,
    queryParams?: Record<string, string>,
  ): void {
    const navigate = () =>
      queryParams
        ? this.router.navigate([route], { queryParams })
        : this.router.navigateByUrl(route);

    const currentPath = this.router.url.split('?')[0];
    if (currentPath === route && !queryParams) {
      this.router.navigateByUrl('/', { skipLocationChange: true }).then(navigate);
      return;
    }

    navigate();
  }

  // Popup form methods
  openPartRequestForm() {
    this.isFormOpen = true;
    this.partForm.reset();
    this.fileName = '';
    this.updateBodyScroll();
    // this.notificationService.info('Fill in the form to request a part');
    this.getBrandList();
    this.cdr.markForCheck();
  }

  closePartRequestForm() {
    if (this.partForm.dirty) {
      // this.notificationService.info('Part request form closed. Changes discarded.');
    }
    this.isFormOpen = false;
    this.partForm.reset();
    this.fileName = '';
    this.fileValidationErrors = [];
    this.updateBodyScroll();
    this.cdr.markForCheck();
  }

  private updateBodyScroll(): void {
    if (isPlatformBrowser(this.platformId)) {
      setTimeout(() => {
        if (this.isFormOpen) {
          const scrollY = window.scrollY;
          document.body.style.position = 'fixed';
          document.body.style.top = `-${scrollY}px`;
          document.body.style.width = '100%';
          document.body.style.overflow = 'hidden';
          document.body.setAttribute('data-scroll-y', scrollY.toString());
        } else {
          const scrollY = document.body.getAttribute('data-scroll-y');
          if (scrollY) {
            document.body.style.position = '';
            document.body.style.top = '';
            document.body.style.width = '';
            document.body.style.overflow = '';
            document.body.removeAttribute('data-scroll-y');
            window.scrollTo(0, parseInt(scrollY, 10));
          } else {
            document.body.style.position = '';
            document.body.style.top = '';
            document.body.style.width = '';
            document.body.style.overflow = '';
          }
        }
      }, 0);
    }
  }

  getBrandList() {
    const payload = {
      action_type: 'make',
      make_list: [],
      model_list: [],
      variant_list: [],
      year_of_manufacture: '',
      offset: 0,
      limit: 10,
    };

    this.apiService
      .getBrand(payload)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (res) => {
          if (res.success && Array.isArray(res.data?.Vehicle_Brands_List)) {
            this.partForm.patchValue({
              brand: '',
              vehicleModel: '',
              year: '',
              category: '',
            });
            this.brands = res.data.Vehicle_Brands_List;
            // this.notificationService.success(`${this.brands.length} brands loaded`);
          } else {
            this.brands = [];
            // this.notificationService.error('Failed to load brands');
          }
          this.cdr.markForCheck();
        },
        error: (err) => {
          console.error('Failed to fetch brands', err);
          // this.notificationService.error(
          //   err?.error?.data?.msg ||
          //     'Failed to fetch brands. Please try again.',
          // );
          this.cdr.markForCheck();
        },
      });
  }

  onBrandSelect(event: any) {
    const selectedBrand =
      event.target?.value || this.partForm.get('brand')?.value;

    if (!selectedBrand) {
      this.modelList = [];
      this.yearList = [];
      this.categoryList = [];
      this.partForm.patchValue({ vehicleModel: '', year: '', category: '' });
      return;
    }

    const payload = {
      action_type: 'model',
      make_list: [selectedBrand],
      model_list: [],
      variant_list: [],
      year_of_manufacture: '',
      offset: 0,
      limit: 10,
    };

    this.apiService
      .getBrand(payload)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (res) => {
          if (res.data) {
            this.modelList = res.data?.Vehicle_Models_List.map(
              (item: { PARENT_MODEL_s: string }) => ({
                model_name: item.PARENT_MODEL_s,
              }),
            );
            // this.notificationService.success(
            //   `${this.modelList.length} models loaded for ${selectedBrand}`,
            // );
          } else {
            this.modelList = [];
            // this.notificationService.error(
            //   'No models found for selected brand',
            // );
          }

          this.yearList = [];
          this.categoryList = [];
          this.partForm.patchValue({
            vehicleModel: '',
            year: '',
            category: '',
          });
          this.cdr.markForCheck();
        },
        error: (err) => {
          console.error('Failed to fetch models', err);
          // this.notificationService.error(
          //   err?.error?.data?.msg ||
          //     'Failed to fetch models. Please try again.',
          // );
          this.modelList = [];
          this.yearList = [];
          this.categoryList = [];
          this.cdr.markForCheck();
        },
      });
  }

  onModelSelect(event: any) {
    const selectedBrand = this.partForm.get('brand')?.value;
    const selectedModel =
      event.target?.value || this.partForm.get('vehicleModel')?.value;

    if (!selectedBrand || !selectedModel) {
      this.yearList = [];
      this.categoryList = [];
      this.partForm.patchValue({ year: '', category: '' });
      return;
    }

    const payload = {
      action_type: 'year_of_manufacture',
      make_list: [selectedBrand],
      model_list: [selectedModel],
      variant_list: [],
      year_of_manufacture: '',
      offset: 0,
      limit: 10,
    };

    this.apiService
      .getBrand(payload)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (res) => {
          if (res.data) {
            this.yearList = res.data?.Vehicle_Year.map(
              (item: { YEAR_OF_MANUFACTURE_s: string }) =>
                item.YEAR_OF_MANUFACTURE_s,
            );
            // this.notificationService.success(
            //   `${this.yearList.length} years available`,
            // );
          } else {
            this.yearList = [];
            // this.notificationService.error('No years found for selected model');
          }

          this.categoryList = [];
          this.partForm.patchValue({ year: '', category: '' });
          this.cdr.markForCheck();
        },
        error: (err) => {
          console.error('Failed to fetch years', err);
          // this.notificationService.error(
          //   err?.error?.data?.msg || 'Failed to fetch years. Please try again.',
          // );
          this.yearList = [];
          this.categoryList = [];
          this.cdr.markForCheck();
        },
      });
  }

  onYearSelect(event: any) {
    const selectedBrand = this.partForm.get('brand')?.value;
    const selectedModel = this.partForm.get('vehicleModel')?.value;
    const selectedYear =
      event.target?.value || this.partForm.get('year')?.value;

    if (!selectedBrand || !selectedModel || !selectedYear) {
      this.categoryList = [];
      this.partForm.patchValue({ category: '' });
      return;
    }

    const payload = {
      action_type: 'variant',
      make_list: [selectedBrand],
      model_list: [selectedModel],
      variant_list: [],
      year_of_manufacture: selectedYear,
      offset: 0,
      limit: 10,
    };

    this.apiService
      .getBrand(payload)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (res) => {
          if (res.data) {
            this.categoryList = res.data?.Vehicle_Variant_List.map(
              (item: { VARIANT_s: string }) => ({
                category_name: item.VARIANT_s,
              }),
            );
            // this.notificationService.success(
            //   `${this.categoryList.length} variants available`,
            // );
          } else {
            this.categoryList = [];
            // this.notificationService.error(
            //   'No variants found for selected year',
            // );
          }
          this.partForm.patchValue({ category: '' });
          this.cdr.markForCheck();
        },
        error: (err) => {
          console.error('Failed to fetch categories', err);
          // this.notificationService.error(
          //   err?.error?.data?.msg ||
          //     'Failed to fetch variants. Please try again.',
          // );
          this.categoryList = [];
          this.cdr.markForCheck();
        },
      });
  }

  onCategorySelect(event: any) {
    const selectedBrand = this.partForm.get('brand')?.value;
    const selectedModel = this.partForm.get('vehicleModel')?.value;
    const selectedYear = this.partForm.get('year')?.value;
    const selectedCategory =
      event.target?.value || this.partForm.get('category')?.value;

    if (
      !selectedBrand ||
      !selectedModel ||
      !selectedYear ||
      !selectedCategory
    ) {
      this.partForm.patchValue({ category: '' });
      return;
    }
  }

  onFileSelected(event: any) {
    const files = event.target.files;
    this.fileValidationErrors = [];
    if (files && files.length > 0) {
      const file = files[0];

      this.fileName = file.name;

      const validation = this.validateFile(file);
      if (!validation.isValid) {
        this.fileValidationErrors.push(validation.error!);
        // this.notificationService.error(validation.error!);
        this.fileName = '';
        this.cdr.markForCheck();
        return;
      }

      this.partForm.patchValue({
        attachment: file,
      });
      this.notificationService.success(
        `File "${file.name}" attached successfully`,
      );
      this.cdr.markForCheck();
    }
  }

  private validateFile(file: File): { isValid: boolean; error?: string } {
    const allowedTypes = [
      'image/jpeg',
      'image/jpg',
      'image/png',
      'application/pdf',
    ];
    if (/^\s/.test(file.name)) {
      return {
        isValid: false,
        error: `File name should not start with a space.`,
      };
    }
    if (!allowedTypes.includes(file.type)) {
      return {
        isValid: false,
        error: `File "${file.name}" is not a valid format. Only PDF, JPG and PNG files are allowed.`,
      };
    }
    const maxSize = 5 * 1024 * 1024;
    if (file.size > maxSize) {
      return {
        isValid: false,
        error: `File "${file.name}" is too large. Maximum file size is 5MB.`,
      };
    }
    return { isValid: true };
  }

  alphaNumberOnly(event: any) {
    var inp = String.fromCharCode(event.keyCode);
    const data = /[a-zA-Z0-9 ]/;
    if (data.test(inp)) {
      return true;
    } else {
      event.preventDefault();
      return false;
    }
  }

  preventLeadingSpace(event: KeyboardEvent, input: any) {
    const key = event.key;
    if (key === ' ' && (!input.value || input.selectionStart === 0)) {
      event.preventDefault();
      return;
    }
  }

  onSubmitenquiry() {
    if (this.partForm.valid) {
      this.isSubmitting = true;

      const formData = new FormData();
      formData.append('part_name', this.partForm.get('partName')?.value || '');
      formData.append('brand', this.partForm.get('brand')?.value || '');
      formData.append(
        'vehicle_model',
        this.partForm.get('vehicleModel')?.value || '',
      );
      formData.append(
        'year_of_manufacturing',
        this.partForm.get('year')?.value || '',
      );
      formData.append('category', this.partForm.get('category')?.value || '');
      formData.append('remarks', this.partForm.get('remarks')?.value || '');

      const file = this.partForm.get('attachment')?.value;
      if (file && file instanceof File) {
        formData.append('image_file', file, file.name);
      }
      this.loaderService.show();

      this.apiService
        .enquiryNow(formData)
        .pipe(takeUntil(this.destroy$))
        .subscribe({
          next: (res) => {
            if (res.success) {
              // this.notificationService.success(
              //   'Part request submitted successfully! We will contact you soon.',
              // );
              this.closePartRequestForm();
              this.partForm.reset();
            } else {
              // this.notificationService.error(
              //   res.message ||
              //     'Failed to submit part request. Please try again.',
              // );
            }
            this.loaderService.hide();
            this.cdr.markForCheck();
          },
          error: (err) => {
            console.error('Error submitting part request:', err);
            // this.notificationService.error(
            //   err?.error?.data?.msg ||
            //     'Failed to submit part request. Please try again.',
            // );
            this.loaderService.hide();
            this.cdr.markForCheck();
          },
          complete: () => {
            this.isSubmitting = false;
            this.loaderService.hide();
          },
        });
    } else {
      Object.keys(this.partForm.controls).forEach((key) => {
        this.partForm.get(key)?.markAsTouched();
      });
      // this.notificationService.error(
      //   'Please fill in all required fields correctly',
      // );
      this.cdr.markForCheck();
    }
  }
}
